/**
 * The bearer token the shell's `ApiClient` sends, read from whatever Firebase
 * app the auth provider initialised.
 *
 * This module initialises nothing. It reads the default app if one exists and
 * answers null if one does not, which is what makes it safe to import before
 * sign-in has been wired up and safe to call on a signed-out page: `ApiClient`
 * turns a null token into `{kind: 'unauthenticated'}` locally instead of
 * sending a request the API would have to reject.
 *
 * A fresh token is read per request rather than cached -- Firebase ID tokens
 * last an hour and `getIdToken()` refreshes transparently, so caching one here
 * would serve an expired token after the first hour.
 */

import { getApps } from 'firebase/app';
import { getAuth } from 'firebase/auth';

import { ApiClient, type TokenSource } from '../../lib/api';
import { loadConfig, type Config } from '../../lib/config';

export const firebaseIdToken: TokenSource = async () => {
  // getAuth() throws when no app has been initialised. That is a normal state
  // here -- a test renders the shell without Firebase, and the entry point
  // imports this module before the provider runs -- so it is a guard, not a
  // defensive flourish.
  if (getApps().length === 0) return null;
  const user = getAuth().currentUser;
  return user === null ? null : user.getIdToken();
};

/**
 * Both arguments are defaulted rather than closed over, so a test can supply a
 * base URL and a token without reaching for `import.meta.env` or standing up a
 * Firebase app. `main.tsx` calls this with neither.
 */
export function createAppApiClient(
  config: Config = loadConfig(),
  token: TokenSource = firebaseIdToken,
): ApiClient {
  return new ApiClient(config.apiBaseUrl, token);
}
